"use client";

import { useEffect, useRef, useState } from "react";
import { useMatterportRuntime } from "./use-matterport-runtime";
import { useLaunchViewerSession, useLaunchViewerSessionControls } from "./launch-viewer-session-context";
import { buildMatterportEmbedUrl } from "@/lib/virtualspace/matterport";
import type { MatterportCapabilitiesSdk } from "@/lib/virtualspace/matterport-sdk-capabilities";

type LaunchSdk = MatterportCapabilitiesSdk & {
  disconnect?: () => void;
  App?: NonNullable<MatterportCapabilitiesSdk["App"]> & {
    state?: { subscribe(callback: (state: { phase: string }) => void): { cancel(): void } };
  };
};
type LoadStage = "loading" | "starting" | "ready" | "errored";

function prefersReducedMotion() {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

function TourPreview({ url }: { url?: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (!url || failedUrl === url) return null;
  try {
    const parsed = new URL(url, "https://preview.invalid");
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  } catch { return null; }
  // Existing, resolved cover only. Loading the tour never creates a thumbnail job.
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={url} alt="" onError={() => setFailedUrl(url)} className="absolute inset-0 h-full w-full object-cover opacity-35" />;
}

/** Public Launch only. Restricted guest tours own their fail-closed lifecycle. */
export function MatterportLaunchViewer(props: {
  modelId: string; sdkKey: string; previewUrl?: string | null;
}) {
  return <LaunchViewerAttempt key={props.modelId} {...props} />;
}

function LaunchViewerAttempt({ modelId, sdkKey, previewUrl }: {
  modelId: string; sdkKey: string; previewUrl?: string | null;
}) {
  const [failure, setFailure] = useState<string | null>(null);
  const fallback = failure === modelId;
  const fallbackUrl = buildMatterportEmbedUrl(modelId);
  if (!fallback) return <MatterportSdkLaunchBlock modelId={modelId} sdkKey={sdkKey} previewUrl={previewUrl} onError={() => setFailure(modelId)} />;
  return <div className="relative h-full w-full bg-black" data-vs-matterport-fallback>
    {fallbackUrl && <iframe src={fallbackUrl} title="Matterport walkthrough" className="h-full w-full border-0" allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen" allowFullScreen />}
    <div className="absolute left-1/2 top-[calc(max(1rem,env(safe-area-inset-top))+4rem)] z-20 w-[min(90%,28rem)] -translate-x-1/2 rounded-2xl border border-white/15 bg-black/90 p-5 backdrop-blur-xl text-center text-sm text-white shadow-lg">
      <p role="status">Tour tools couldn’t load. Retry to restore them.</p>
      <button type="button" onClick={() => setFailure(null)} className="mt-3 min-h-11 rounded-xl bg-white px-4 text-sm font-medium text-black hover:bg-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Retry tour</button>
    </div>
  </div>;
}

/** Each attempt owns its iframe, readiness observer, deadline, and cleanup. */
export function MatterportSdkLaunchBlock({ modelId, sdkKey, previewUrl, onError }: {
  modelId: string;
  sdkKey: string;
  previewUrl?: string | null;
  onError?: (err: unknown) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onErrorRef = useRef(onError);
  const [snapshot, setSnapshot] = useState<{ modelId: string; sdk: LaunchSdk | null; stage: LoadStage } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const stage = snapshot?.modelId === modelId ? snapshot.stage : "loading";
  const viewerSdk = snapshot?.modelId === modelId ? snapshot.sdk : null;
  const { features, metadata } = useMatterportRuntime(viewerSdk);
  const reducedMotion = useReducedMotion();
  useLaunchViewerSession({ sdk: viewerSdk, modelId, reducedMotion });
  const publishedControls = useLaunchViewerSessionControls();
  const controls = stage === "ready" && publishedControls?.modelId === modelId ? publishedControls : null;
  const choosingPosition = controls?.canResume || controls?.busy;
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!modelId || !sdkKey) {
      setSnapshot({ modelId, sdk: null, stage: "errored" });
      onErrorRef.current?.(new Error("The tour configuration is unavailable."));
      return;
    }
    let active = true;
    let connectedSdk: LaunchSdk | null = null;
    let phaseSubscription: { cancel(): void } | undefined;
    const container = document.createElement("div");
    container.className = "w-full h-full";
    containerRef.current.appendChild(container);
    setSnapshot({ modelId, sdk: null, stage: "loading" });

    function disconnect(sdk: LaunchSdk | null) {
      try { sdk?.disconnect?.(); } catch { /* Already disconnected. */ }
    }
    function cancelPhase() {
      try { phaseSubscription?.cancel(); } catch { /* SDK may already be disconnected. */ }
      phaseSubscription = undefined;
    }
    function fail(err: unknown) {
      if (!active) return;
      active = false;
      clearTimeout(timeout);
      cancelPhase();
      disconnect(connectedSdk);
      connectedSdk = null;
      container.remove();
      setSnapshot({ modelId, sdk: null, stage: "errored" });
      // Vendor errors may include keys. Never expose their raw message to logs/UI.
      // eslint-disable-next-line no-console
      console.warn("[matterport-sdk-launch-block] setup failed");
      onErrorRef.current?.(err);
    }
    function ready(sdk: LaunchSdk) {
      if (!active) return;
      clearTimeout(timeout);
      timeout = undefined;
      setSnapshot({ modelId, sdk, stage: "ready" });
    }
    let timeout: ReturnType<typeof setTimeout> | undefined = setTimeout(() => fail(new Error("Matterport viewer setup timed out.")), 30_000);

    (async () => {
      try {
        const loadSdkModule = new Function("url", "return import(url);") as (url: string) => Promise<{
          setupSdk: typeof import("@matterport/sdk").setupSdk;
        }>;
        const { setupSdk } = await loadSdkModule("/vendor/matterport-sdk.js");
        if (!active) return;
        const sdk = await setupSdk(sdkKey, {
          space: modelId,
          container,
          iframeAttributes: {
            width: "100%", height: "100%", frameborder: "0",
            allow: "fullscreen; xr-spatial-tracking; gyroscope; accelerometer; web-share; autoplay",
            title: "Matterport walkthrough",
            style: "display: block; border: 0; width: 100%; height: 100%;",
          },
          iframeQueryParams: {
            qs: 1, play: 1, help: 0, vr: 0, hr: 0, share: 0, mls: 2, brand: 0, title: 0,
            csnapshot: 0, cDisableTagDock: 1, cDisableTagDockPaging: 1,
            cdlttype: prefersReducedMotion() ? "instant" : "fly",
            cdlttime: prefersReducedMotion() ? 0 : 800,
          },
        }) as unknown as LaunchSdk;
        if (!active) { disconnect(sdk); return; }
        connectedSdk = sdk;
        if (sdk.App?.state?.subscribe) {
          // Setup can finish before the tour is interactive. Keep the deadline
          // until PLAYING; the SDK's phase is not a download percentage.
          const subscription = sdk.App.state.subscribe(({ phase }) => {
            if (!active) return;
            if (phase === "appphase.playing") ready(sdk);
            else if (phase === "appphase.error") fail(new Error("Matterport could not load the tour."));
            else {
              // A provider may reload after first becoming interactive. Bound
              // that wait too instead of leaving an inert overlay indefinitely.
              timeout ??= setTimeout(() => fail(new Error("Matterport viewer reload timed out.")), 30_000);
              setSnapshot({ modelId, sdk: null, stage: phase === "appphase.starting" ? "starting" : "loading" });
            }
          });
          if (active) phaseSubscription = subscription;
          else subscription.cancel();
        } else ready(sdk);
      } catch (err) { fail(err); }
    })();

    return () => {
      active = false;
      clearTimeout(timeout);
      cancelPhase();
      disconnect(connectedSdk);
      container.remove();
    };
  }, [modelId, sdkKey, attempt]);

  return <div className="w-full h-full bg-black relative"
    data-vs-matterport-sdk-status={stage === "starting" ? "loading" : stage}
    data-vs-matterport-splat={features.splatPresent === undefined ? "unknown" : String(features.splatPresent)}
    data-vs-matterport-mesh={features.meshPresent === undefined ? "unknown" : String(features.meshPresent)}
    data-vs-matterport-version={metadata?.applicationVersion}>
    <div ref={containerRef} className="h-full w-full" inert={stage !== "ready" || choosingPosition} aria-hidden={stage !== "ready" || choosingPosition ? true : undefined} />
    {controls && choosingPosition && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 p-6 text-center text-white">
      <div className="w-full max-w-sm rounded-2xl border border-white/15 bg-black/90 p-5 shadow-xl backdrop-blur-xl">
        <p className="text-sm">{controls.busy ? "Opening your position…" : "Continue where you left off?"}</p>
        {!controls.busy && <div className="mt-4 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={controls.resume} className="min-h-11 rounded-xl bg-white px-4 text-sm font-medium text-black hover:bg-white/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Resume</button>
          <button type="button" onClick={controls.startOver} className="min-h-11 rounded-xl border border-white/30 px-4 text-sm text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Start over</button>
        </div>}
      </div>
    </div>}
    {stage !== "ready" && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/80 p-6 text-center text-white">
      <TourPreview url={previewUrl} />
      <div className="relative w-full max-w-sm rounded-2xl border border-white/15 bg-black/90 p-5 shadow-xl backdrop-blur-xl">
        <p role="status" className="text-sm">{stage === "errored" ? "The tour couldn’t load." : stage === "starting" ? "Opening your tour…" : "Loading your tour…"}</p>
        {stage === "errored" && <button type="button" onClick={() => setAttempt(value => value + 1)} className="mt-3 min-h-11 rounded-xl bg-white px-4 text-sm font-medium text-black hover:bg-white/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Retry tour</button>}
      </div>
    </div>}
  </div>;
}
